import { ValidationError } from '@netfoundry-ui/shared/model';
import { isEmpty, isNil } from 'lodash';

/**
 * The answer to a pre-submit check, arranged so a form can ask it about one field. The service
 * answers a write carrying the `nf-validate` header with 200 and a flat array of
 * {@link ValidationError}, creating nothing.
 *
 * Messages attach on the path, never on the label: two matchers in one request both label their
 * fault `op`, so a form keyed on labels would show one matcher's message under another's box.
 * Immutable, and cheap to ask: the work happens once, in the constructor.
 */
export class ValidationErrorIndex {
    /** Every message returned, by exact path, in the order the service listed them. */
    private readonly byPath = new Map<string, string[]>();

    /** The bare paths that also arrived indexed, so the duplicate can be dropped. */
    private readonly indexedParents = new Set<string>();

    constructor(private readonly errors: ValidationError[] = []) {
        for (const error of errors || []) {
            const path = (error?.path || '').trim();
            if (isEmpty(path) || isEmpty(error?.message)) {
                continue;
            }
            const messages = this.byPath.get(path) || [];
            messages.push(error.message);
            this.byPath.set(path, messages);

            const parent = parentOfIndexed(path);
            if (!isNil(parent)) {
                this.indexedParents.add(parent);
            }
        }
    }

    /** True when the request would be accepted as it stands. */
    get isClean(): boolean {
        return this.byPath.size === 0;
    }

    /**
     * What to show under one field, or ''. One sentence rather than a list, because a field
     * carrying two faults is answered by fixing the first.
     *
     * A bare collection path answers nothing while anything indexed under it was returned:
     * `$.componentIds[1]` is the box that is wrong, and `$.componentIds` is the same fault
     * repeated for a caller that draws no box per index.
     */
    messageFor(path: string): string {
        if (this.indexedParents.has(path)) {
            return '';
        }
        return (this.byPath.get(path) || [])[0] || '';
    }

    /** Whether a field has anything against it. */
    has(path: string): boolean {
        return !isEmpty(this.messageFor(path));
    }

    /**
     * Everything no field on the form claims, as sentences for the form's own error block. The
     * claimed paths are given rather than inferred, because only the form knows which boxes it drew.
     *
     * The message alone. A label and a path say which field a fault belongs to; they are not part
     * of what the reader is shown.
     */
    unclaimed(claimedPaths: string[] = []): string[] {
        const claimed = new Set(claimedPaths);
        return (this.errors || [])
            .filter((error) => {
                const path = (error?.path || '').trim();
                if (isEmpty(path) || isEmpty(error?.message)) {
                    return false;
                }
                return !claimed.has(path) && !this.indexedParents.has(path);
            })
            .map((error) => error.message);
    }
}

/**
 * The collection path an indexed one belongs to, or undefined. `$.componentIds[1]` is
 * `$.componentIds`. `$.matchers[1].op` belongs to nothing here, because the service only duplicates
 * an error whose path ends in an index.
 */
export function parentOfIndexed(path: string): string | undefined {
    if (!path.endsWith(']')) {
        return undefined;
    }
    const start = path.lastIndexOf('[');
    return start > 0 ? path.slice(0, start) : undefined;
}
